import { jsonConstants } from '@thoughtspot/json-constants';
import chroma from 'chroma-js';
import _ from 'lodash';
import { getSerie, getSeries, getSeriesColor, getXAxisLabelAt, } from '../data/data-util';
import { ChartThemeService } from '../theme/chart-theme-service';
import { ChartType, } from '../types';
import { getChartType, getColorScaleOfMultiColorSeriesColors, getLegendColumns, getMultiColorSeriesColors, getRadialColumn, getSeriesColorsMap, getUpdatedSeriesColor, getXAxisColumns, getXAxisColumnsHash, getYAxisColumns, setColorScaleOfMultiColorSeriesColors, } from './chart-model-utils';
import { customColorSelectorConfig, getLegendLabels, isGeoChartType, isLineStackedColumnChart, } from './chart-type-specification-service';
import { getName } from './column-name-util';
export function addAlphaToColor(color) {
    return chroma(color)
        .alpha(0.5)
        .css();
}
export const convertToAlpha = (colors) => {
    return colors.map(function (color) {
        return addAlphaToColor(color);
    });
};
export const getUpdatedColorSelectConfigByColumn = (chartModel, options, colorSelectOptions, columnId) => {
    let updatedColorSelectOptions = [];
    if (options.allowedConfigurations.configureColorForXAttrs) {
        getXAxisColumns(chartModel).forEach(col => {
            if (col.id === columnId) {
                updatedColorSelectOptions = [...colorSelectOptions];
            }
        });
    }
    else if (options.allowedConfigurations.configureColorForRadialAttrs) {
        updatedColorSelectOptions = colorSelectOptions;
    }
    else if (getLegendColumns(chartModel).length === 0) {
        getYAxisColumns(chartModel).forEach((col, index) => {
            if (col.id === columnId) {
                updatedColorSelectOptions.push(colorSelectOptions[index]);
            }
        });
    }
    else if (isLineStackedColumnChart(chartModel.config.chartType)) {
        getYAxisColumns(chartModel).forEach((col, index) => {
            if (col.id === columnId && index > 0) {
                updatedColorSelectOptions.push(colorSelectOptions[index - 1]);
            }
        });
        const legendColumnIds = getLegendColumns(chartModel).map(col => col.id);
        if (updatedColorSelectOptions.length === 0 &&
            legendColumnIds.indexOf(columnId) >= 0) {
            const skipColorCount = getYAxisColumns(chartModel).length - 1;
            updatedColorSelectOptions = colorSelectOptions.splice(skipColorCount);
        }
    }
    else {
        updatedColorSelectOptions = colorSelectOptions;
    }
    return updatedColorSelectOptions;
};
export const getColorConfigForLegendLabels = (chartModel, options, dataModel, updateClientStateProperty) => {
    return _.reduce(getLegendLabels(chartModel, dataModel), (result, legendLabel) => {
        const label = legendLabel.label;
        const colorSelectOption = {
            caption: label,
            selectedColor: getSeriesColor(chartModel, dataModel, label),
            closeCallback: (color) => {
                const serieId = getSerie(dataModel, label).blinkSeriesId;
                if (options.useAlphaColors) {
                    color = addAlphaToColor(color);
                }
                const updatedSerieColor = getUpdatedSeriesColor(getSeriesColorsMap(chartModel), serieId, color);
                updateClientStateProperty(chartModel.vizProp, [jsonConstants.chartClientStateKeys.SERIES_COLORS], updatedSerieColor);
            },
        };
        if (colorSelectOption.selectedColor) {
            result.push(colorSelectOption);
        }
        return result;
    }, []);
};
export const getColorConfigForXAttributes = (chartModel, options, dataModel, getTranslatedText, updateClientStateProperty) => {
    return _.reduce(dataModel.xValueToRawValues, (result, rawValue, idx) => {
        const label = getXAxisLabelAt(chartModel, dataModel, idx, undefined, undefined, getTranslatedText);
        const colorSelectOption = {
            caption: label,
            selectedColor: getSeriesColor(chartModel, dataModel, label),
            closeCallback: (color) => {
                if (options.useAlphaColors) {
                    color = addAlphaToColor(color);
                }
                const updatedSerieColor = getUpdatedSeriesColor(getSeriesColorsMap(chartModel), label, color);
                updateClientStateProperty(chartModel.vizProp, [jsonConstants.chartClientStateKeys.SERIES_COLORS], updatedSerieColor);
            },
        };
        if (colorSelectOption.selectedColor) {
            result.push(colorSelectOption);
        }
        return result;
    }, []);
};
export const getScaleFromColor = (color) => {
    return [
        chroma(color)
            .brighten()
            .hex(),
        chroma(color)
            .darken()
            .hex(),
    ];
};
export const getDefaultChartsColorScale = (chartType) => {
    const maxColor = chroma(ChartThemeService.getDefaultTheme().primaryColors[1]).hex();
    const minColorMap = {
        [ChartType.Heatmap]: chroma(maxColor)
            .brighten(2)
            .hex(),
        [ChartType.Treemap]: chroma(ChartThemeService.getDefaultTheme().primaryColors[0]).hex(),
    };
    const minColor = minColorMap[chartType] || '#ffffff';
    return [minColor, maxColor];
};
export const getColorScaleForSerie = (chartModel, dataModel) => {
    var _a, _b, _c;
    if (!((_a = chartModel.vizProp.seriesColors) === null || _a === void 0 ? void 0 : _a.length)) {
        return getDefaultChartsColorScale(getChartType(chartModel));
    }
    const series = getSeries(dataModel);
    const serieName = (_b = series[0]) === null || _b === void 0 ? void 0 : _b.name;
    const color = (_c = getSeriesColor(chartModel, dataModel, serieName)) === null || _c === void 0 ? void 0 : _c.trim();
    return color
        ? getScaleFromColor(color)
        : getDefaultChartsColorScale(getChartType(chartModel));
};
export const getColorConfigForRadialChart = (chartModel, dataModel, updateClientStateProperty) => {
    const radialColumn = getRadialColumn(chartModel);
    const series = getSeries(dataModel);
    const serieName = series[0].name;
    const colorSelectOption = {
        caption: radialColumn && getName(radialColumn),
        selectedColor: getSeriesColor(chartModel, dataModel, serieName),
        closeCallback: color => {
            const serieId = getSerie(dataModel, serieName).blinkSeriesId;
            const updatedSerieColor = getUpdatedSeriesColor(getSeriesColorsMap(chartModel), serieId, color);
            updateClientStateProperty(chartModel.vizProp, [jsonConstants.chartClientStateKeys.SERIES_COLORS], updatedSerieColor);
        },
    };
    return colorSelectOption.selectedColor ? [colorSelectOption] : [];
};
export const getColorConfigForMultiColorSeriesChart = (chartModel, dataModel, updateClientStateProperty) => {
    const xColumnHash = getXAxisColumnsHash(chartModel);
    return _.reduce(getLegendLabels(chartModel, dataModel), (result, legend) => {
        const label = legend.label;
        const multiColorSeriesMap = getMultiColorSeriesColors(chartModel);
        const serieId = getSerie(dataModel, label).blinkSeriesId;
        const colorScale = getColorScaleOfMultiColorSeriesColors(multiColorSeriesMap, serieId, xColumnHash);
        const colorSelectOption = {
            caption: label,
            selectedColor: !_.isEmpty(colorScale)
                ? colorScale[Math.floor(colorScale.length / 2)]
                : '',
            closeCallback: color => {
                const multiColorSeriesColors = getMultiColorSeriesColors(chartModel);
                const updatedColorSeries = setColorScaleOfMultiColorSeriesColors(multiColorSeriesColors, serieId, xColumnHash, getScaleFromColor(color));
                updateClientStateProperty(chartModel.vizProp, [
                    jsonConstants.chartClientStateKeys
                        .MULTI_COLOR_SERIES_COLORS,
                ], updatedColorSeries);
            },
        };
        if (colorSelectOption.selectedColor) {
            result.push(colorSelectOption);
        }
        return result;
    }, []);
};
export const getSeriesIdFromSeriesColors = (chartModel, caption, dataModel) => {
    var _a;
    const seriesId = (_a = getSerie(dataModel, caption)) === null || _a === void 0 ? void 0 : _a.blinkSeriesId;
    const isMultiColorSeries = isGeoChartType(chartModel.config.chartType);
    const seriesColors = isMultiColorSeries
        ? chartModel.vizProp.multiColorSeriesColors
        : chartModel.vizProp.seriesColors;
    const isCaptionIncluded = seriesColors.some(serieColor => { var _a; return ((_a = serieColor === null || serieColor === void 0 ? void 0 : serieColor.serieName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === (caption === null || caption === void 0 ? void 0 : caption.toLowerCase()); });
    const isSerieIdIncluded = seriesColors.some(serieColor => { var _a; return ((_a = serieColor === null || serieColor === void 0 ? void 0 : serieColor.serieName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === (seriesId === null || seriesId === void 0 ? void 0 : seriesId.toLowerCase()); });
    if (isCaptionIncluded) {
        return caption === null || caption === void 0 ? void 0 : caption.toLowerCase();
    }
    if (isSerieIdIncluded) {
        return seriesId === null || seriesId === void 0 ? void 0 : seriesId.toLowerCase();
    }
    return '';
};
export const resetChartColors = (chartModel, dataModel, colorSelectOptions, defaultColors, options, section = undefined) => {
    const clonedClientState = _.cloneDeep(chartModel.vizProp);
    colorSelectOptions.forEach((colorOption, idx) => {
        if (colorOption.section !== section) {
            return;
        }
        const seriesId = getSeriesIdFromSeriesColors(chartModel, colorOption.caption, dataModel);
        if (colorOption.selectedColor !== defaultColors[idx].selectedColor) {
            if (options.allowedConfigurations.useCustomColorSelector) {
                clonedClientState.customColorSelectorArray[idx] =
                    defaultColors[idx].selectedColor;
            }
            else if (getChartType(chartModel) === ChartType.GeoArea) {
                clonedClientState.multiColorSeriesColors = setColorScaleOfMultiColorSeriesColors(clonedClientState.multiColorSeriesColors, seriesId, getXAxisColumnsHash(chartModel), getScaleFromColor(defaultColors[idx].selectedColor));
            }
            else {
                clonedClientState.seriesColors = getUpdatedSeriesColor(clonedClientState.seriesColors, seriesId, defaultColors[idx].selectedColor);
            }
        }
    });
    return clonedClientState;
};
export const configureColors = (chartModel, options, dataModel, getTranslatedText, updateClientStateProperty, columnId) => {
    const colorsConfig = [];
    if (!columnId && options.allowedConfigurations.useCustomColorSelector) {
        const result = customColorSelectorConfig(chartModel.config.chartType, chartModel, updateClientStateProperty, getTranslatedText);
        return _.reduce(result, (resultObject, colorDataObject) => {
            const colorSelectOption = {
                section: colorDataObject.section,
                caption: colorDataObject.label,
                selectedColor: colorDataObject.color,
                closeCallback: color => {
                    colorDataObject.closeCallback(color);
                },
            };
            if (colorSelectOption.selectedColor) {
                resultObject.push(colorSelectOption);
            }
            else {
            }
            return resultObject;
        }, colorsConfig);
    }
    if (options.allowedConfigurations.configureColorForXAttrs) {
        return getColorConfigForXAttributes(chartModel, options, dataModel, getTranslatedText, updateClientStateProperty);
    }
    if (options.allowedConfigurations.configureColorForRadialAttrs) {
        return getColorConfigForRadialChart(chartModel, dataModel, updateClientStateProperty);
    }
    if (chartModel.config.chartType === ChartType.GeoArea) {
        return getColorConfigForMultiColorSeriesChart(chartModel, dataModel, updateClientStateProperty);
    }
    return getColorConfigForLegendLabels(chartModel, options, dataModel, updateClientStateProperty);
};
export const getColorSelectOptions = (chartModel, options, getTranslatedText, chart, updateClientStateProperty, columnId) => {
    if (!chart) {
        return [];
    }
    const colorSelectOptions = configureColors(chartModel, options, chart.getDataModel(), getTranslatedText, updateClientStateProperty, columnId);
    if (_.size(colorSelectOptions) === 0) {
        return [];
    }
    if (!columnId) {
        return colorSelectOptions;
    }
    return getUpdatedColorSelectConfigByColumn(chartModel, options, colorSelectOptions, columnId);
};
export const isLegendColumnStyleDisplayed = (chartModel, columnId) => {
    return _.some(getLegendColumns(chartModel), col => col.id === columnId);
};
export const isYAxisColumnStyleDisplayed = (chartModel, options, columnId) => {
    const chartType = chartModel.config.chartType;
    if (!options.allowedConfigurations.configureColorForYAttrs ||
        options.allowedConfigurations.configureColorForXAttrs) {
        return false;
    }
    const isYAxisColumn = _.some(getYAxisColumns(chartModel), col => col.id === columnId);
    const showYAxisColumnStyle = !getLegendColumns(chartModel).length ||
        isLineStackedColumnChart(chartType);
    return showYAxisColumnStyle && isYAxisColumn;
};
export const isXAxisColumnStyleDisplayed = (chartModel, options, columnId) => {
    if (!options.allowedConfigurations.configureColorForXAttrs) {
        return false;
    }
    return _.some(getXAxisColumns(chartModel), col => col.id === columnId);
};
export const isRadialColumnStyleDisplayed = (chartModel, options, columnId) => {
    const radialColumn = getRadialColumn(chartModel);
    return !!(radialColumn &&
        radialColumn.id === columnId &&
        options.allowedConfigurations.configureColorForRadialAttrs);
};
export const getSystemSeriesColors = (chartModel) => {
    if (isGeoChartType(chartModel.config.chartType)) {
        return _.cloneDeep(chartModel.vizProp.systemMultiColorSeriesColors);
    }
    return _.cloneDeep(chartModel.vizProp.systemSeriesColors);
};
export const getBrightenedColor = (color, brightnessLevel = 3) => {
    return chroma(color)
        .brighten(brightnessLevel)
        .hex();
};
//# sourceMappingURL=chart-color-utils.js.map